import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { search } from "../lib/api";

export default function SearchDialog({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [setOpen]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setBusy(true);
    const t = setTimeout(() => {
      search(q)
        .then((d) => setResults(d.results || []))
        .catch(() => setResults([]))
        .finally(() => setBusy(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const grouped = results.reduce((acc, r) => {
    acc[r.type] = acc[r.type] || [];
    acc[r.type].push(r);
    return acc;
  }, {});

  const go = (url) => {
    setOpen(false);
    setQ("");
    navigate(url);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search destinations, experiences, journeys, visas, journal…"
        value={q}
        onValueChange={setQ}
        data-testid="universal-search-input"
      />
      <CommandList className="max-h-[70vh]">
        {q.trim().length < 2 && (
          <div className="px-4 py-8 text-center text-sm text-[var(--muted-ink)]">
            <SearchIcon className="mx-auto mb-3 h-5 w-5 text-[var(--brass)]" />
            Try &ldquo;Japan&rdquo;, &ldquo;safari&rdquo;, &ldquo;Schengen&rdquo; or &ldquo;honeymoon&rdquo;
          </div>
        )}
        {q.trim().length >= 2 && !busy && results.length === 0 && (
          <CommandEmpty>No matches. Try a country, city or travel style.</CommandEmpty>
        )}
        {Object.entries(grouped).map(([type, items]) => (
          <CommandGroup key={type} heading={type}>
            {items.map((r) => (
              <CommandItem
                key={`${r.collection}-${r.url}`}
                value={`${r.title} ${r.subtitle} ${type}`}
                onSelect={() => go(r.url)}
                data-testid="universal-search-result-item"
                className="flex cursor-pointer items-center gap-3 py-2.5"
              >
                {r.image && (
                  <img
                    src={r.image}
                    alt=""
                    className="h-10 w-14 shrink-0 rounded-md object-cover"
                    loading="lazy"
                  />
                )}
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-[var(--ink)]">
                    {r.title}
                  </span>
                  {r.subtitle && (
                    <span className="block truncate text-xs text-[var(--muted-ink)]">
                      {r.subtitle}
                    </span>
                  )}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
